import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CpUcConstraintFunctionsService } from '@se/cp.usercontrols/cp-uc-constraints-custom-functions/cp-uc-constraint-functions.service';
import { EstensaWaitingService, ReportingDataBuilder } from '@se/platform';
import { SeConstraintCheckerService } from '@se/constraint';
import { firstValueFrom, Subscription } from 'rxjs';
import { delay, finalize, map, tap } from 'rxjs/operators';

import { CpDatasetService } from './cp-dataset-service';
import { CpDatasourceService } from './cp-datasource.service';
import { CpModelService } from './cp-model.service';
import { CpNodeService } from './cp-node.service';
import { CpReportService } from './cp-report.service';
import { cpInitialize, cpSafeExit } from './store/cp.actions';

import * as fromCp from './store/cp.state';

@Injectable({
    providedIn: 'root',
})
export class CpUiEngineService {
    private imagingSubscription?: Subscription;

    constructor(
        private constraintChecker: SeConstraintCheckerService,
        private datasetService: CpDatasetService,
        private dataSourceService: CpDatasourceService,
        private modelService: CpModelService,
        private nodeService: CpNodeService,
        private reportService: CpReportService,
        private reportingDataBuilder: ReportingDataBuilder,
        private store: Store<fromCp.RootState>,
        private waitingService: EstensaWaitingService,
        private constraintFunctionsService: CpUcConstraintFunctionsService,
        private translateService: TranslateService,
    ) {}

    async initCustomPage() {
        return firstValueFrom(this.reportingDataBuilder.getMasterDataResolver().getMasterData()).then(
            ({ constraints, dataset, dataSources, idCustomPage, idModelTypes, layout, model, placeholders }) => {
                this.datasetService.initialize(dataset);
                this.dataSourceService.setDataSources(dataSources);
                this.constraintFunctionsService.init(
                    this.modelService,
                    this.nodeService,
                    this.dataSourceService,
                    this.translateService,
                );
                this.constraintChecker.addCustomFunctions(this.constraintFunctionsService.functionsEntries);
                this.modelService.initialize(model, constraints, placeholders);
                this.reportService.initDefaultValues(model, layout.autoNormalValues);
                this.nodeService.initialize(layout);

                this.store.dispatch(
                    cpInitialize({
                        page: {
                            idCustomPage,
                            idCustomPageRoot: this.reportingDataBuilder.getMasterDataResolver().getCustomPageRoot(),
                            idModelTypes,
                            modules: layout.modules,
                            sideComponents: layout.seSideComponents ?? [],
                            leftSliderClosed: Boolean(layout.leftSliderClosed),
                            rightSliderClosed: layout.rightSliderClosed ? Boolean(layout.rightSliderClosed) : true,
                            rightSliderEnabled: Boolean(layout.rightSliderEnabled),
                        },
                        isPreview: model.IsPreview as boolean,
                    }),
                );
            },
        );
    }

    async changeLayout(idCustomPage: string) {
        this.waitingService.isWaiting = true;

        return firstValueFrom(
            this.reportingDataBuilder
                .getMasterDataResolver()
                .getLayout(idCustomPage)
                .pipe(
                    finalize(() => (this.waitingService.isWaiting = false)),
                    map(({ constraints, dataSources, idModelTypes, layout }) => {
                        this.dataSourceService.setDataSources(dataSources);
                        this.modelService.initializeConstraints(constraints);

                        this.nodeService.safeExit();

                        return { idModelTypes, layout };
                    }),
                    delay(1),
                    tap(({ idModelTypes, layout }) => {
                        this.nodeService.initialize(layout);
                        this.store.dispatch(
                            cpInitialize({
                                page: {
                                    idCustomPage,
                                    idCustomPageRoot: this.reportingDataBuilder
                                        .getMasterDataResolver()
                                        .getCustomPageRoot(),
                                    idModelTypes,
                                    modules: layout.modules,
                                    sideComponents: layout.seSideComponents ?? [],
                                    rightSliderClosed: Boolean(layout.rightSliderClosed),
                                    leftSliderClosed: Boolean(layout.leftSliderClosed),
                                    rightSliderEnabled: Boolean(layout.rightSliderEnabled),
                                },
                            }),
                        );
                    }),
                ),
        );
    }

    safeExit() {
        this.store.dispatch(cpSafeExit());
        this.imagingSubscription?.unsubscribe();
    }
}
